import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

/**
 * Footer.
 *
 * Architecture is driven by what actually exists in this app. Ten links
 * previously pointed at anchor ids and pages that were never built
 * (#lubricants, #concrete, #composites, #rubber, #research, #docs, #downloads,
 * #faq, #privacy, #terms) and silently dropped the visitor at the top of a page.
 *
 * Rather than four equal columns of filler, the layout is weighted: the brand
 * and contact details carry real content on the left, navigation is compact on
 * the right. Nothing here links anywhere that does not exist.
 */

const columns = [
  {
    heading: 'Applications',
    links: [
      { label: 'Lubricant additives', href: '/services' },
      { label: 'Concrete enhancement', href: '/services' },
      { label: 'Composite materials', href: '/services' },
      { label: 'Rubber applications', href: '/services' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About Micratto', href: '/about' },
      { label: 'News', href: '/news' },
      { label: 'Contact', href: '/contact' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Case studies', href: '/portfolio' },
      { label: 'Technical data sheets', href: '/services' },
      { label: 'Downloads', href: '/services' },
    ],
  },
];

const Footer = () => {
  return (
    <footer className="bg-dark-rich text-white">
      {/* Accent seam ties the footer to the brand instead of a plain border. */}
      <div aria-hidden="true" className="seam-accent" />
      <div className="container-page py-14 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          {/* Brand + positioning + contact */}
          <div className="min-w-0 lg:col-span-5">
            <Link
              href="/"
              className="inline-flex items-center rounded-sm"
              aria-label="Micratto — home"
            >
              <AppImage
                src="/assets/images/micratto-logo-white.png"
                alt="Micratto"
                width={150}
                height={41}
                className="h-auto max-w-full object-contain bg-transparent"
                isLogo
              />
            </Link>

            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
              Graphene-enhanced nanomaterial additives for lubricants, concrete, composites and
              rubber — engineered to improve existing materials without complete reformulation.
            </p>

            <div className="mt-7 space-y-1.5 text-sm">
              <p className="text-xs uppercase tracking-wider text-slate-500">Headquarters</p>
              <address className="not-italic leading-relaxed text-slate-400">
                Micratto Technologies Sdn. Bhd.
                <br />
                9, Jalan Seri Rejang 4, Kuala Lumpur 53300, Malaysia
              </address>
              <a
                href="mailto:operations@micratto.com"
                className="inline-block break-all pt-1 text-slate-300 underline-offset-4 transition-colors duration-fast hover:text-white hover:underline"
              >
                operations@micratto.com
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="grid min-w-0 grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-6 lg:col-start-7">
            {columns.map((col) => (
              <nav key={col.heading} aria-labelledby={`footer-${col.heading}`} className="min-w-0">
                <h2
                  id={`footer-${col.heading}`}
                  className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500"
                >
                  {col.heading}
                </h2>
                <ul className="mt-4 space-y-3">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        href={link.href}
                        className="link-u text-sm text-slate-400 transition-colors duration-fast hover:text-white"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-7 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500">
            © 2026 Micratto Technologies Sdn. Bhd. All rights reserved.
          </p>
          <a
            href="https://www.linkedin.com/company/micratto"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-12 w-12 items-center justify-center rounded-md border border-white/20 bg-white/[0.04] transition-[border-color,background-color,transform] duration-fast hover:border-accent/60 hover:bg-white/10 active:translate-y-px motion-reduce:active:transform-none"
            aria-label="Micratto on LinkedIn"
          >
            <AppImage
              src="/assets/images/linkedin.webp?v=2"
              alt=""
              width={40}
              height={40}
              className="h-10 w-10 object-contain bg-transparent"
              isLogo
            />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
